import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Heart, Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { datadogRum } from '@datadog/browser-rum';
import CataloguesAPI, { Catalog, CatalogItem } from '../../api/CataloguesAPI';
import { toastService } from '../../services/ToastService';
import { Skeleton } from '../../components/Skeleton/Skeleton';
import { useCatalogSaves } from '../../hooks/useCatalogSaves';
import { useScrollToTop } from '../../hooks/useScroolToTop';
import PublicCatalogLayout from '../../components/PublicCatalog/PublicCatalogLayout';
import PublicCatalogHero from '../../components/PublicCatalog/PublicCatalogHero';
import PublicCatalogLookGrid from '../../components/PublicCatalog/PublicCatalogLookGrid';
import PublicCatalogItemDetail from '../../components/PublicCatalog/PublicCatalogItemDetail';
import PublicCatalogImportDialogs from '../../components/PublicCatalog/PublicCatalogImportDialogs';
import PublicCatalogNotFound from '../../components/PublicCatalog/PublicCatalogNotFound';
import { PublicCatalogActionFab } from '../../components/PublicCatalog/PublicCatalogCtas';

function CatalogSingleShowRoomPage() {
  const { someCatalogueId, itemId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isLoggedIn = useSelector((state: any) => state.settings.value.isLoggedIn);
  const [catalog, setCatalog] = useState<Catalog | null>(null);
  const { savedIds, toggleSave, isSaved } = useCatalogSaves(catalog?.uuid);
  const [isLoading, setIsLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [isUserActionLoading, setIsUserActionLoading] = useState(false);
  const [showSelection, setShowSelection] = useState(false);
  const [displayLoginSignUpFlow, setDisplayLoginSignUpFlow] = useState(false);
  const [successfullyImported, setSuccessfullyImported] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSavedOnly, setShowSavedOnly] = useState(false);
  
  const catalogRef = useRef(catalog);
  catalogRef.current = catalog;

  useScrollToTop();

  const activeItem = useMemo(
    () => catalog?.items?.find((item) => item.uuid === itemId) || null,
    [catalog, itemId]
  );

  const loadCatalog = useCallback(async () => {
    if (!someCatalogueId) {
      setIsLoading(false);
      setLoadFailed(true);
      return;
    }
    const loaded = catalogRef.current;
    if (loaded && (loaded.uuid === someCatalogueId || loaded.slug?.trim() === someCatalogueId)) {
      return;
    }
    setIsLoading(true);
    setLoadFailed(false);
    try {
      const catalogResponse = await CataloguesAPI.getCatalog(someCatalogueId);
      const nextCatalog = catalogResponse.data;
      if (!nextCatalog?.uuid && !nextCatalog?.name) {
        setCatalog(null);
        setLoadFailed(true);
        return;
      }
      setCatalog(nextCatalog);
      document.title = nextCatalog.name || document.title;
      CataloguesAPI.recordView(someCatalogueId).catch((error) => {
        console.error('Failed to record catalog view', { catalogId: someCatalogueId, error });
      });
    } catch (error) {
      console.error('Failed to load public catalog', { catalogId: someCatalogueId, error });
      setCatalog(null);
      setLoadFailed(true);
    } finally {
      setIsLoading(false);
    }
  }, [someCatalogueId]);

  useEffect(() => {
    loadCatalog();
  }, [loadCatalog]);

  useEffect(() => {
    const slug = catalog?.slug?.trim();
    if (!slug || !someCatalogueId || slug === someCatalogueId) {
      return;
    }
    const next = itemId ? `/catalogues/${slug}/${itemId}` : `/catalogues/${slug}`;
    navigate(next, { replace: true });
  }, [catalog?.slug, someCatalogueId, itemId, navigate]);

  useEffect(() => {
    const importSuccess = searchParams.get('importSuccess');
    if (importSuccess === 'true') {
      showSuccessPopup();
      setSearchParams({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const visibleItems = useMemo(() => {
    const items = catalog?.items || [];
    const query = searchQuery.trim().toLowerCase();
    return items.filter((item) => {
      const matchesQuery = !query || item.name?.toLowerCase().includes(query);
      const matchesSaved = !showSavedOnly || (item.uuid && savedIds.has(item.uuid));
      return matchesQuery && matchesSaved;
    });
  }, [catalog?.items, searchQuery, showSavedOnly, savedIds]);

  const showSuccessPopup = () => {
    datadogRum.addAction('Catalog Successfully imported');
    setShowSelection(false);
    setDisplayLoginSignUpFlow(false);
    setSuccessfullyImported(true);
    setTimeout(() => {
      setShowConfetti(true);
    }, 250);
  };

  const closeSuccessPopup = () => {
    setSuccessfullyImported(false);
    setShowConfetti(false);
  };

  const importCatalog = async () => {
    if (catalog?.items && catalog.items.length > 0) {
      setIsUserActionLoading(true);
      try {
        await CataloguesAPI.import(someCatalogueId);
        setSearchParams({ importSuccess: 'true' });
      } catch (error) {
        console.error('Failed to import catalog', { catalogId: someCatalogueId, error });
        toastService.showToast(t('toast.errors.catalogues.import_failed'), 'error');
      } finally {
        setIsUserActionLoading(false);
      }
    } else {
      toastService.showToast(t('toast.errors.catalogues.not_found'), 'error');
    }
  };

  const handleImportCatalog = async () => {
    datadogRum.addAction('Import Catalog button clicked');
    if (isUserActionLoading) {
      return;
    }
    if (!isLoggedIn) {
      setShowSelection(false);
      setDisplayLoginSignUpFlow(true);
      return;
    }
    await importCatalog();
  };

  const handleCardClick = (item: CatalogItem) => {
    navigate(`/catalogues/${someCatalogueId}/${item.uuid}`);
  };

  return (
    <PublicCatalogLayout>
      {isLoading || (!catalog && !loadFailed) ? (
        <div className="space-y-6">
          <Skeleton className="h-[50vh] w-full rounded-none" />
          <div className="mx-auto grid max-w-7xl grid-cols-2 gap-4 px-4 md:grid-cols-3">
            {[...Array(6)].map((_, index) => (
              <Skeleton key={index} className="aspect-[3/4] w-full rounded-2xl" />
            ))}
          </div>
        </div>
      ) : loadFailed || !catalog ? (
        <PublicCatalogNotFound onRetry={loadCatalog} />
      ) : activeItem ? (
        <PublicCatalogItemDetail
          catalog={catalog}
          item={activeItem}
          saved={isSaved(activeItem.uuid)}
          onBack={() => navigate(`/catalogues/${someCatalogueId}`)}
          onImport={() => setShowSelection(true)}
          onToggleSave={() => toggleSave(activeItem.uuid)}
        />
      ) : (
        <div>
          <PublicCatalogHero catalog={catalog} onImport={() => setShowSelection(true)} />
          <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
            <div className="mb-8 flex flex-col gap-3 sm:flex-row sm:items-center">
              <label className="relative flex-1">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="search"
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  placeholder={t('marketplace.catalogues.lookbook.search')!}
                  className="h-12 w-full rounded-full border border-as-pink-200 bg-white/70 pl-10 pr-4 text-sm text-foreground outline-none ring-as-pink-200 focus:ring-2 dark:bg-white/10"
                />
              </label>
              <button
                type="button"
                onClick={() => setShowSavedOnly((current) => !current)}
                className={`inline-flex h-12 items-center justify-center gap-2 rounded-full border px-4 text-sm font-semibold ${
                  showSavedOnly
                    ? 'border-as-pink-300 bg-as-pink-300 text-white'
                    : 'border-as-pink-200 bg-white/70 text-as-pink-500 dark:bg-white/10'
                }`}
              >
                <Heart className={`h-4 w-4 ${showSavedOnly ? 'fill-current' : ''}`} />
                {t('marketplace.catalogues.lookbook.saved_looks')}
                {savedIds.size > 0 ? ` (${savedIds.size})` : ''}
              </button>
            </div>
            {visibleItems.length > 0 ? (
              <PublicCatalogLookGrid
                items={visibleItems}
                isSaved={isSaved}
                onOpen={handleCardClick}
                onToggleSave={toggleSave}
              />
            ) : (
              <p className="py-16 text-center text-muted-foreground">
                {showSavedOnly
                  ? t('marketplace.catalogues.lookbook.no_saved')
                  : t('marketplace.catalogues.lookbook.empty_search')}
              </p>
            )}
          </div>
        </div>
      )}

      {catalog && (
        <PublicCatalogActionFab
          catalog={catalog}
          onImport={() => setShowSelection(true)}
          fromItem={Boolean(activeItem)}
        />
      )}

      <PublicCatalogImportDialogs
        showSelection={showSelection}
        onCloseSelection={() => setShowSelection(false)}
        onImport={handleImportCatalog}
        isImporting={isUserActionLoading}
        showLogin={displayLoginSignUpFlow}
        onCloseLogin={() => setDisplayLoginSignUpFlow(false)}
        onLoginSuccess={importCatalog}
        showSuccess={successfullyImported}
        showConfetti={showConfetti}
        onCloseSuccess={closeSuccessPopup}
        onGoToOrders={() => navigate('/orders?openForm=true')}
      />
    </PublicCatalogLayout>
  );
}

export default CatalogSingleShowRoomPage;
